import React from 'react'
import { getParseDataFromHtmlString } from 'src/consumer/helpers/utilFunctions'
import { formatLabel, formatEmrValue, hasValue } from './emrDisplayUtils'
import {
  SectionCard,
  SectionHeader,
  SectionContent,
  SubSectionCard,
} from './SectionCard'

/**
 * Simple display component for EMR section data
 * Just renders sections → subsections → fields. KISS principle.
 */
/**
 * Normalize EMR section data to handle different API response formats
 * Handles both:
 * 1. Direct format: { sectionCode, subSections, ... }
 * 2. Wrapped format: { id, bookedEventId, sectionCode, subSections, ... }
 * 3. Array of sections format: [{ sectionCode, subSections }, ...]
 */
function normalizeEmrSectionDataList(emrSectionDataList) {
  if (!emrSectionDataList || emrSectionDataList.length === 0) {
    return []
  }

  // If it's already an array of sections with sectionCode and subSections, use as-is
  const normalized = emrSectionDataList.map((item) => {
    // If item already has sectionCode and subSections, it's in the correct format
    if (item.sectionCode && Array.isArray(item.subSections)) {
      return item
    }
    
    // If item has subSections but no sectionCode, try to extract sectionCode from other fields
    if (Array.isArray(item.subSections)) {
      return {
        ...item,
        sectionCode: item.sectionCode || item.section || item.id,
      }
    }
    
    // If item doesn't have subSections, it might be a wrapper - return as-is and let filtering handle it
    return item
  })

  return normalized
}

export default function EmrSectionsListDisplay({ emrSectionDataList, emrSectionPrintSettings = {} }) {
  if (!emrSectionDataList || emrSectionDataList.length === 0) {
    return null
  }

  // Normalize the data to handle different formats
  const normalizedData = normalizeEmrSectionDataList(emrSectionDataList)

  // Filter and map sections
  const sectionsToDisplay = normalizedData
    .filter((section) => {
      // Check if section has subsections
      if (!section?.subSections || section.subSections.length === 0) {
        return false
      }
      
      // Check if section is enabled for printing (if settings are provided)
      const sectionId = section.id || section.sectionCode
      if (sectionId && Object.keys(emrSectionPrintSettings).length > 0) {
        // If settings exist, check if this section is printable
        const isPrintable = emrSectionPrintSettings[sectionId] !== false
        if (!isPrintable) {
          return false
        }
      }
      
      // Include section if at least one subsection has data
      const hasAnyData = section.subSections.some((subSection) => {
        const values = subSection.values || {}
        return Object.keys(values).some((fieldCode) => {
          const emrValue = values[fieldCode]
          return hasValue(emrValue)
        })
      })
      
      return hasAnyData
    })

  if (sectionsToDisplay.length === 0) {
    return null
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0', paddingTop: '0' }}>
      {sectionsToDisplay.map((section, sectionIndex) => {
        // Get section title - try multiple possible fields
        const sectionTitle = section.title || 
                            section.sectionTitle || 
                            formatLabel(section.sectionCode) ||
                            formatLabel(section.id) ||
                            `Section ${sectionIndex + 1}`
        
        return (
          <SectionCard
            key={section.id || section.sectionCode || `section-${sectionIndex}`}
            $sectionType="emrSection"
          >
            <SectionHeader $sectionType="emrSection">
              {sectionTitle}
            </SectionHeader>
            <SectionContent>
              {/* Subsections */}
              {section.subSections
                .filter((subSection) => {
                  const values = subSection.values || {}
                  const hasData = Object.keys(values).some((fieldCode) =>
                    hasValue(values[fieldCode]),
                  )
                  return hasData
                })
                .map((subSection, subSectionIndex) => (
                  <SubSectionDisplay
                    key={subSection?.uid || subSection?.subSectionCode || subSection?.id || `subSection-${subSectionIndex}`}
                    subSection={subSection}
                    subSectionIndex={subSectionIndex}
                  />
                ))}
            </SectionContent>
          </SectionCard>
        )
      })}
    </div>
  )
}

/**
 * Display a single subsection with its fields
 */
function SubSectionDisplay({ subSection }) {
  const values = subSection.values || {}

  // Get field codes that have values
  const fieldCodesWithValues = Object.keys(values).filter((fieldCode) => hasValue(values[fieldCode]))

  if (fieldCodesWithValues.length === 0) {
    return null
  }

  // Get subsection title
  const subSectionTitle = subSection.subSectionLabel || formatLabel(subSection.subSectionCode)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '12px' }}>
      {/* Subsection Title */}
      <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '8px', color: '#475569' }}>
        {subSectionTitle}
      </div>

      {/* Fields */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', paddingLeft: '12px' }}>
        {fieldCodesWithValues.map((fieldCode) => {
          const emrValue = values[fieldCode]
          const fieldLabel = emrValue.valueLabel || emrValue.title || formatLabel(fieldCode)
          const isRichText = emrValue.format === 'richtext' && emrValue.valueString
          const fieldValue = isRichText
            ? null
            : formatEmrValue(emrValue)

          return (
            <div key={fieldCode} style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '14px', lineHeight: '1.6' }}>
              <span style={{ fontWeight: '500', color: '#334155' }}>{fieldLabel}:</span>
              {isRichText ? (
                <>
                  <style>{`
                    .emr-richtext-content p { margin: 0 0 0.5em 0; }
                    .emr-richtext-content p:empty { min-height: 1.6em; margin: 0 0 0.5em 0; }
                    .emr-richtext-content ul, .emr-richtext-content ol { margin: 0.5em 0; padding-left: 1.5em; }
                    .emr-richtext-content strong { font-weight: 600; }
                    .emr-richtext-content em { font-style: italic; }
                  `}</style>
                  <div
                    className="emr-richtext-content free-form-rich-text-view tiptap-content"
                    style={{ color: '#1e293b', marginTop: '4px' }}
                  >
                    {getParseDataFromHtmlString(emrValue.valueString, { preserveEmptyParagraphs: true })}
                  </div>
                </>
              ) : (
                <span style={{ color: '#1e293b' }}>{fieldValue}</span>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
